import { ContactModal } from '@/features/contact-modal';
import { OrganizationFormModal } from '@/features/organization-form-modal';
import { FieldGroup } from '@/components/ui/field';

import { useContactSelector } from '../model';
import { OrganizationSelect } from './organization-select';
import { ContactSelect } from './contact-select';
import { ContactDetails } from './contact-details';

interface ContactSelectorProps {
  /**
   * Whole labels, not a shared stem: legacy names these fields as complete
   * phrases (`Sender Organization` / `Sender Contact`) because the wording is
   * not a suffix in every language — Spanish says «Organización Remitente» and
   * «Contacto del Remitente».
   */
  organizationLabel: string;
  contactLabel: string;
  organizationId?: string;
  organizationName?: string;
  contactId?: string;
  contactName?: string;
  onOrganizationChange: (id: string | undefined) => void;
  onContactChange: (id: string | undefined) => void;
  className?: string;
  disabled?: boolean;
  required?: boolean;
  organizationError?: string;
  contactError?: string;
  /** Query the organization field starts with, e.g. the name an SRF import could not resolve. */
  organizationInitialQuery?: string;
  /** Query the contact field starts with, e.g. the name an SRF import matched but never saved. */
  contactInitialQuery?: string;
}

export const ContactSelector = ({
  organizationLabel,
  contactLabel,
  organizationId,
  organizationName,
  contactId,
  contactName,
  onOrganizationChange,
  onContactChange,
  className,
  disabled = false,
  required = false,
  organizationError,
  contactError,
  organizationInitialQuery,
  contactInitialQuery
}: ContactSelectorProps) => {
  const {
    // State
    isContactModalOpen,
    showContactDetails,
    isLoadingOrgs,
    isLoadingContacts,
    orgOptions,
    contactOptions,
    selectedContact,
    contactModalMode,
    isOrgModalOpen,
    orgModalMode,
    // Handlers
    handleContactAdd,
    handleContactOpen,
    handleContactModalClose,
    handleContactModalSuccess,
    toggleContactDetails,
    handleOrgOpen,
    handleOrgModalClose,
    handleOrganizationChange
  } = useContactSelector({
    organizationId,
    organizationName,
    contactId,
    contactName,
    onOrganizationChange,
    onContactChange,
    disabled
  });

  return (
    <div className={className}>
      <FieldGroup>
        <OrganizationSelect
          label={organizationLabel}
          organizationId={organizationId}
          options={orgOptions}
          isLoading={isLoadingOrgs}
          disabled={disabled}
          required={required}
          error={organizationError}
          initialQuery={organizationInitialQuery}
          onChange={handleOrganizationChange}
          handleOrgOpen={handleOrgOpen}
        />

        <ContactSelect
          label={contactLabel}
          contactId={contactId}
          options={contactOptions}
          isLoading={isLoadingContacts}
          disabled={disabled}
          required={required}
          error={contactError}
          initialQuery={contactInitialQuery}
          showDetails={showContactDetails}
          onChange={onContactChange}
          onDetailsClick={toggleContactDetails}
          onEditClick={handleContactOpen}
          onAddClick={handleContactAdd}
          canAdd={!!organizationId}
        />
      </FieldGroup>

      {selectedContact && showContactDetails && <ContactDetails contact={selectedContact} />}

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={handleContactModalClose}
        onSuccess={handleContactModalSuccess}
        contactId={contactId}
        orgId={organizationId ? Number(organizationId) : undefined}
        mode={contactModalMode}
      />

      <OrganizationFormModal
        isOpen={isOrgModalOpen}
        onClose={handleOrgModalClose}
        organizationId={Number(organizationId)}
        mode={orgModalMode}
        onSuccess={handleOrgModalClose}
      />
    </div>
  );
};
